/**
 * Persistent configuration for timeline options (markers, agents).
 *
 * Each setting is stored via `useProperty` under the "timeline" id namespace
 * so values persist in the store across unmounts. Settings are intentionally
 * global — they apply to all samples/timelines as shared user preferences
 * rather than per-sample state.
 *
 * `branchesPresent` shifts the *unset* default for `showBranches`: when the
 * user has never explicitly toggled it and the current sample contains
 * branches, branches are shown by default. An explicit user toggle (stored
 * value) always wins.
 */

import { useCallback, useMemo } from "react";

import { useProperty } from "@tsmono/react/hooks";

import { defaultMarkerConfig } from "../markers";
import type { MarkerConfig, MarkerDepth, MarkerKind } from "../markers";

import type { TimelineOptions } from "./useTimeline";

// =============================================================================
// Types
// =============================================================================

export interface UseTimelineConfigResult {
  /** Resolved MarkerConfig for the timeline pipeline. */
  markerConfig: MarkerConfig;
  /** Resolved TimelineOptions for the timeline pipeline. */
  agentConfig: TimelineOptions;

  /** Current marker kinds selection. */
  markerKinds: MarkerKind[];
  /** Current marker depth selection. */
  markerDepth: MarkerDepth;
  /** Whether utility agents are shown. */
  includeUtility: boolean;
  /** Whether branches are shown as swimlane rows. */
  showBranches: boolean;
  /** Whether branches are positioned at their fork point. */
  forkRelative: boolean;
  /** Whether branches containing only structural events are shown. */
  showEmptyBranches: boolean;

  setMarkerKinds: (kinds: MarkerKind[]) => void;
  setMarkerDepth: (depth: MarkerDepth) => void;
  setIncludeUtility: (include: boolean) => void;
  setShowBranches: (show: boolean) => void;
  setForkRelative: (forkRelative: boolean) => void;
  setShowEmptyBranches: (show: boolean) => void;
  toggleMarkerKind: (kind: MarkerKind) => void;
  resetToDefaults: () => void;
  /** True when all settings match their defaults. */
  isDefault: boolean;
}

// =============================================================================
// Defaults
// =============================================================================

const kDefaultMarkerKinds = defaultMarkerConfig.kinds;
const kDefaultMarkerDepth = defaultMarkerConfig.depth;
const kDefaultIncludeUtility = false;
const kDefaultShowBranches = false;
const kDefaultForkRelative = false;
const kDefaultShowEmptyBranches = false;

// =============================================================================
// Hook
// =============================================================================

export interface UseTimelineConfigOptions {
  /**
   * Whether the current sample contains any branches. When true, `showBranches`
   * defaults to true unless the user has an explicit stored preference.
   */
  branchesPresent?: boolean;
}

export function useTimelineConfig(
  options: UseTimelineConfigOptions = {}
): UseTimelineConfigResult {
  const { branchesPresent = false } = options;

  const [storedKinds, setStoredKinds, removeStoredKinds] = useProperty<
    MarkerKind[]
  >("timeline", "markerKinds");
  const [storedDepth, setStoredDepth, removeStoredDepth] =
    useProperty<MarkerDepth>("timeline", "markerDepth");
  const [storedUtility, setStoredUtility, removeStoredUtility] =
    useProperty<boolean>("timeline", "includeUtility");
  const [storedShowBranches, setStoredShowBranches, removeStoredShowBranches] =
    useProperty<boolean>("timeline", "showBranches");
  const [storedForkRelative, setStoredForkRelative, removeStoredForkRelative] =
    useProperty<boolean>("timeline", "forkRelative");
  const [
    storedShowEmptyBranches,
    setStoredShowEmptyBranches,
    removeStoredShowEmptyBranches,
  ] = useProperty<boolean>("timeline", "showEmptyBranches");

  const markerKinds = storedKinds ?? kDefaultMarkerKinds;
  const markerDepth = storedDepth ?? kDefaultMarkerDepth;
  const includeUtility = storedUtility ?? kDefaultIncludeUtility;
  // When the user has not explicitly toggled showBranches, default it on for
  // samples that contain branches; otherwise fall back to kDefaultShowBranches.
  const showBranches =
    storedShowBranches ?? (branchesPresent || kDefaultShowBranches);
  // Default fork-relative to on when branches are shown and the user hasn't
  // explicitly toggled it (storedForkRelative is undefined).
  const forkRelative =
    storedForkRelative ?? (showBranches || kDefaultForkRelative);
  const showEmptyBranches =
    storedShowEmptyBranches ?? kDefaultShowEmptyBranches;

  const markerConfig: MarkerConfig = useMemo(
    () => ({ kinds: markerKinds, depth: markerDepth }),
    [markerKinds, markerDepth]
  );

  const agentConfig: TimelineOptions = useMemo(
    () => ({ includeUtility, showBranches, forkRelative, showEmptyBranches }),
    [includeUtility, showBranches, forkRelative, showEmptyBranches]
  );

  // True when the user has not customized any setting (no stored overrides).
  // Resolved values may still differ from kDefault* via auto-defaults
  // (e.g. branchesPresent flipping showBranches on).
  const isDefault =
    storedKinds === undefined &&
    storedDepth === undefined &&
    storedUtility === undefined &&
    storedShowBranches === undefined &&
    storedForkRelative === undefined &&
    storedShowEmptyBranches === undefined;

  const toggleMarkerKind = useCallback(
    (kind: MarkerKind) => {
      const current = storedKinds ?? kDefaultMarkerKinds;
      const next = current.includes(kind)
        ? current.filter((k) => k !== kind)
        : [...current, kind];
      setStoredKinds(next);
    },
    [storedKinds, setStoredKinds]
  );

  const resetToDefaults = useCallback(() => {
    removeStoredKinds();
    removeStoredDepth();
    removeStoredUtility();
    removeStoredShowBranches();
    removeStoredForkRelative();
    removeStoredShowEmptyBranches();
  }, [
    removeStoredKinds,
    removeStoredDepth,
    removeStoredUtility,
    removeStoredShowBranches,
    removeStoredForkRelative,
    removeStoredShowEmptyBranches,
  ]);

  return {
    markerConfig,
    agentConfig,
    markerKinds,
    markerDepth,
    includeUtility,
    showBranches,
    forkRelative,
    showEmptyBranches,
    setMarkerKinds: setStoredKinds,
    setMarkerDepth: setStoredDepth,
    setIncludeUtility: setStoredUtility,
    setShowBranches: setStoredShowBranches,
    setForkRelative: setStoredForkRelative,
    setShowEmptyBranches: setStoredShowEmptyBranches,
    toggleMarkerKind,
    resetToDefaults,
    isDefault,
  };
}
